import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Checkbox from "src/components/form/Checkbox";
import { useFormikContext } from "formik";

const FEATURES_PERMISSIONS = {
  capture_inventory: "Mobile Asset View",
  import_assets: "Import Assets",
  export_assets: "Export Assets",
  inventory_tracking: "Edit Inventory Counts and Orders",
  reporting: "Reports",
  // capture_packing: "Capture packing",
  // asset_tracking: "Asset tracking",
};

const WRITE_PERMISSIONS = {
  write: "Edit",
  remove: "Remove",
};

const CRUD_PERMISSIONS = {
  read: "View",
  write: "Edit",
  remove: "Remove",
};

const CLIENTS_PERMISSIONS = {
  read: "View",
  write: "Edit",
  remove: "Remove",
};

const PAGES_PERMISSIONS = {
  dashboard: "Assets Page",
  shopping: "Store (View-Only)",
  shopping_cart: "Store (Ordering Enabled)",
};

const FILTER_FIELD2_PROFILE_PERMISSIONS = {
  sharing_tab: "Sharing Tab",
  settings_tab: "Settings Tab",
};

const INSIGHTS_PERMISSIONS = {
  page: "Insights Page",
};

const WAREHOUSE_PERMISSIONS = {
  page: "Warehouse Page",
};

const PERMISSION_TOOLTIPS = {
  dashboard: "Create and edit inventory, adjust quantities, and manage asset details.",
  shopping: "View customer inventory, but cannot place outbound orders.",
  shopping_cart: "View and request inventory via the customer portal.",
};

const ROLE_TOOLTIPS = {
  client: "Full access to inventory and ordering for their organization.",
  employee: "Can view inventory and place orders, but cannot edit assets.",
  manager:
    "Full access to inventory and ordering. Auto-assigned to new customers in their warehouse.",
  inventory: "Access to inventory and fulfillment tools for daily warehouse tasks.",
  billing_user: "Read-only internal access to assigned project orders and billing reports.",
  viewer: "Can view inventory but does not have editting permissions.",
  shopper: "Can place orders via the customer storefront only.",
};

export const ROLES = {
  client: "Customer Admin",
  employee: "Inventory Lead",
  manager: "Warehouse Manager",
  inventory: "Warehouse Staff",
  billing_user: "Billing User",
  shopper: "Inventory Shopper",
  viewer: "Inventory Viewer",
  guest: "Guest",
};

export const getTabs = settings => [
  {
    id: "pages",
    label: "User Views",
    features: PAGES_PERMISSIONS,
  },
  {
    id: "features",
    label: "Features",
    features: FEATURES_PERMISSIONS,
  },
  {
    id: "assets",
    label: "Assets",
    features: CRUD_PERMISSIONS,
  },
  {
    id: "users",
    label: "Users",
    features: WRITE_PERMISSIONS,
  },
  {
    id: "clients",
    label: settings.filter_field4 || "Clients",
    features: CLIENTS_PERMISSIONS,
  },
  {
    id: "projects",
    label: settings.filter_field3 || "Projects",
    features: WRITE_PERMISSIONS,
  },
  {
    id: "filter_field2_profile",
    label: settings.filter_field2 ? `${settings.filter_field2} Profile` : "Filter field 2 Profile",
    features: FILTER_FIELD2_PROFILE_PERMISSIONS,
  },
  {
    id: "insights",
    label: "Insights",
    features: INSIGHTS_PERMISSIONS,
  },
  {
    id: "warehouse",
    label: "Warehouse",
    features: WAREHOUSE_PERMISSIONS,
  },
];

const styles = {
  headerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
  },
  roleContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 0.5,
  },
  tooltip: {
    fontSize: theme => theme.typography.pxToRem(14),
  },
  infoIcon: {
    fontSize: 18,
    color: "#000000",
    cursor: "pointer",
    "&:hover": {
      color: "#333333",
    },
  },
  roleHeaderCell: {
    width: 200,
    p: 2,
    fontWeight: 600,
    backgroundColor: "#f5f5f5",
  },
  featureHeaderCell: {
    p: 1,
    minWidth: 120,
  },
  roleCell: {
    width: 200,
    p: 2,
    whiteSpace: "nowrap",
    fontWeight: 500,
    backgroundColor: "#fafafa",
  },
  featureCell: {
    p: 1,
    minWidth: 120,
    "& .MuiFormControlLabel-root": {
      mr: 0,
      justifyContent: "center",
    },
  },
};

const TabForm = ({ name, tabKey, roles, features }) => {
  const featureKeys = Object.keys(features);
  const [openTooltip, setOpenTooltip] = useState(null);
  const { setFieldValue, values } = useFormikContext();

  const handleTooltipToggle = (key, event) => {
    event.stopPropagation();
    setOpenTooltip(openTooltip === key ? null : key);
  };

  const handleClickAway = () => {
    setOpenTooltip(null);
  };

  const handleShoppingPermissionChange = (fieldName, checked, roleKey, featureKey) => {
    // Set the clicked field first
    setFieldValue(fieldName, checked);

    // If we're in the pages tab and dealing with shopping permissions and we're checking (not unchecking)
    if (
      tabKey === "pages" &&
      (featureKey === "shopping" || featureKey === "shopping_cart") &&
      checked
    ) {
      const otherFeature = featureKey === "shopping" ? "shopping_cart" : "shopping";
      const otherFieldName = name
        ? `${name}[${roleKey}][${tabKey}][${otherFeature}]`
        : `${roleKey}[${tabKey}][${otherFeature}]`;

      // Uncheck the other one
      setFieldValue(otherFieldName, false);
    }
  };

  const renderHeaderWithTooltip = (key, label) => {
    const tooltipText = PERMISSION_TOOLTIPS[key];

    if (tooltipText) {
      const isOpen = openTooltip === key;

      return (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box sx={styles.headerContainer}>
            <span>{label}</span>
            <Tooltip
              title={tooltipText}
              arrow
              placement="top"
              open={isOpen}
              disableHoverListener
              disableFocusListener
              disableTouchListener
              componentsProps={{
                tooltip: {
                  sx: styles.tooltip,
                },
              }}
            >
              <InfoOutlinedIcon
                sx={styles.infoIcon}
                onClick={event => handleTooltipToggle(key, event)}
              />
            </Tooltip>
          </Box>
        </ClickAwayListener>
      );
    }

    return label;
  };

  const renderRoleWithTooltip = (roleKey, roleLabel) => {
    const tooltipText = ROLE_TOOLTIPS[roleKey];

    if (tooltipText) {
      const isOpen = openTooltip === `role-${roleKey}`;

      return (
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box sx={styles.roleContainer}>
            <span>{roleLabel}</span>
            <Tooltip
              title={tooltipText}
              arrow
              placement="right"
              open={isOpen}
              disableHoverListener
              disableFocusListener
              disableTouchListener
              componentsProps={{
                tooltip: {
                  sx: styles.tooltip,
                },
              }}
            >
              <InfoOutlinedIcon
                sx={styles.infoIcon}
                onClick={event => handleTooltipToggle(`role-${roleKey}`, event)}
              />
            </Tooltip>
          </Box>
        </ClickAwayListener>
      );
    }

    return roleLabel;
  };

  return (
    <TableContainer component={Paper}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell sx={styles.roleHeaderCell}>Role</TableCell>
            {featureKeys.map(key => (
              <TableCell align="center" key={`assets-${key}`} sx={styles.featureHeaderCell}>
                {renderHeaderWithTooltip(key, features[key])}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {roles.map(roleKey => (
            <TableRow key={roleKey}>
              <TableCell sx={styles.roleCell}>
                {renderRoleWithTooltip(roleKey, ROLES[roleKey])}
              </TableCell>
              {featureKeys.map(featureKey => {
                const fieldName = name
                  ? `${name}[${roleKey}][${tabKey}][${featureKey}]`
                  : `${roleKey}[${tabKey}][${featureKey}]`;

                // Check if this is a shopping permission that needs mutual exclusion
                const isShoppingPermission =
                  tabKey === "pages" &&
                  (featureKey === "shopping" || featureKey === "shopping_cart");

                return (
                  <TableCell align="center" key={featureKey} sx={styles.featureCell}>
                    <Checkbox
                      name={fieldName}
                      label={null}
                      onChange={
                        isShoppingPermission
                          ? (fieldName, checked) =>
                              handleShoppingPermissionChange(
                                fieldName,
                                checked,
                                roleKey,
                                featureKey
                              )
                          : undefined
                      }
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TabForm;
